import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "form", "submitButton", "spinner", "progressText", "triggerButton", "formFields"]
  static values = {
    pollUrl: String,
    postUrl: String,
    refreshInterval: { type: Number, default: 2000 },
    mode: { type: String, default: "modal" },
    workingText: { type: String, default: "AI is working..." },
    generatingText: { type: String, default: "AI is generating rules..." },
    doneTemplate: { type: String, default: "Done! Created {count} items. Refreshing..." },
    failedText: { type: String, default: "Generation failed." },
    requestFailedText: { type: String, default: "Request failed. Please try again." }
  }

  open() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
    }
  }

  close() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
    }
  }

  submit(event) {
    event.preventDefault()

    const formData = new FormData(this.formTarget)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Show loading state
    this.submitButtonTarget.disabled = true
    this.spinnerTarget.classList.remove("hidden")
    this.progressTextTarget.textContent = this.workingTextValue

    if (this.hasFormFieldsTarget) {
      this.formFieldsTarget.classList.add("hidden")
    }

    fetch(this.postUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          this.showError(data.error)
        } else {
          this.startPolling()
        }
      })
      .catch(() => {
        this.showError(this.requestFailedTextValue)
      })
  }

  trigger(event) {
    event.preventDefault()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Show inline progress
    this.triggerButtonTarget.classList.add("hidden")
    this.spinnerTarget.classList.remove("hidden")
    this.progressTextTarget.textContent = this.generatingTextValue

    fetch(this.postUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          this.showError(data.error)
          this.triggerButtonTarget.classList.remove("hidden")
        } else {
          this.startPolling()
        }
      })
      .catch(() => {
        this.showError(this.requestFailedTextValue)
        this.triggerButtonTarget.classList.remove("hidden")
      })
  }

  startPolling() {
    this.pollTimer = setInterval(() => this.checkProgress(), this.refreshIntervalValue)
  }

  async checkProgress() {
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      if (data.status === "completed") {
        clearInterval(this.pollTimer)
        this.progressTextTarget.textContent = this.doneTemplateValue
          .replace("{count}", data.created_count || "")
        setTimeout(() => window.location.reload(), 500)
      } else if (data.status === "failed") {
        clearInterval(this.pollTimer)
        const errorMsg = data.errors?.join(", ") || data.error || this.failedTextValue
        this.showError(errorMsg)
      } else if (data.status === "running") {
        this.progressTextTarget.textContent = this.workingTextValue
      }
    } catch (e) {
      // Keep polling on network errors
    }
  }

  showError(message) {
    this.progressTextTarget.textContent = message
    this.spinnerTarget.querySelector(".fa-spinner")?.classList?.add("hidden")

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }

    if (this.hasFormFieldsTarget) {
      this.formFieldsTarget.classList.remove("hidden")
    }

    // Auto-hide error after 5 seconds and reset
    setTimeout(() => {
      if (this.hasTriggerButtonTarget) {
        this.triggerButtonTarget.classList.remove("hidden")
        this.spinnerTarget.classList.add("hidden")
      }
    }, 5000)
  }

  disconnect() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  }
}
